import React from 'react'
import { X } from 'lucide-react'
import CustomSelect from '../../../../../pages/ProjectDocumentation/components/CustomSelect.tsx'
import ModalOverlay from '../../../../UI/ModalOverlay/ModalOverlay.tsx'
import RubroDocumentationBlock from './RubroDocumentationBlock.tsx'
import type {
  DocumentationSlots,
  DraftMetadataFileEntry,
  RubroRow,
  SkillOption
} from '../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import {
  DOCUMENTATION_SLOT_KEYS,
  documentationSlotsHavePages,
  normalizeDocumentationFileIds,
  normalizeDocumentationSlots,
  rubroHasValidSelectedSkill
} from '../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import cardStyles from './ProjectAutofillCard.module.scss'
import styles from './ProjectAutofillRubroDetailModal.module.scss'

const PUBLISHED_HINT =
  'Para editar rubros o datos de la obra publicada, usá «Ir a la obra» y gestioná todo desde la ficha.'

function isRubroFuzzyKind(kind?: string): boolean {
  return String(kind || '').toLowerCase().includes('fuzzy')
}

function isRubroUnresolvedKind(kind?: string): boolean {
  return String(kind || '').toLowerCase() === 'unresolved'
}

export type ProjectAutofillRubroDetailModalProps = {
  isOpen: boolean
  title: string
  rubro: RubroRow | null
  tempId: string
  skillOptions: SkillOption[]
  draftLocked: boolean
  draftId: string | undefined
  isSaving: boolean
  uploadingSlotKey: string | null
  documentationPreview: unknown
  showGlobalDocBar: boolean
  docPdfUrl: string
  docPageCount: number | null | undefined
  metadataFiles: DraftMetadataFileEntry[]
  today: string
  projectStartDate: string
  projectEndDate: string
  advisoryNotes: string[]
  onClose: () => void
  handleRubroSkillSelect: (tempId: string, value: string, rubro: RubroRow) => void
  updateRubro: (tempId: string, updates: Partial<Omit<RubroRow, 'tempId'>>) => void
  handleSelectRubroSlotFileId: (
    tempId: string,
    slot: keyof DocumentationSlots,
    fileId: string | null
  ) => void
  handleRubroSlotPagesChange: (
    tempId: string,
    slot: keyof DocumentationSlots,
    pages: number[]
  ) => void
  handleUploadRubroSlotFile: (
    tempId: string,
    slot: keyof DocumentationSlots,
    file: File
  ) => void
  fuzzyConfirmByRubro: Record<string, boolean>
  setFuzzyConfirmByRubro: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
  rubroUnresolvedNeedsPick: (sp: RubroRow) => boolean
  clampIsoDateToMax: (iso: string, maxIso: string) => string
}

const ProjectAutofillRubroDetailModal: React.FC<ProjectAutofillRubroDetailModalProps> = ({
  isOpen,
  title,
  rubro,
  tempId,
  skillOptions,
  draftLocked,
  draftId,
  isSaving,
  uploadingSlotKey,
  documentationPreview,
  showGlobalDocBar,
  docPdfUrl,
  docPageCount,
  metadataFiles,
  today,
  projectStartDate,
  projectEndDate,
  advisoryNotes,
  onClose,
  handleRubroSkillSelect,
  updateRubro,
  handleSelectRubroSlotFileId,
  handleRubroSlotPagesChange,
  handleUploadRubroSlotFile,
  fuzzyConfirmByRubro,
  setFuzzyConfirmByRubro,
  rubroUnresolvedNeedsPick,
  clampIsoDateToMax
}) => {
  const rid = tempId
  const sp = rubro

  const slots = sp
    ? normalizeDocumentationSlots(sp.documentationSlots)
    : normalizeDocumentationSlots({})
  const idsNorm = sp ? normalizeDocumentationFileIds(sp.documentationFileIds ?? {}) : {}
  const hasSlotFileId = DOCUMENTATION_SLOT_KEYS.some((k) => !!idsNorm[k])
  const showRubroDoc =
    !!sp &&
    (documentationPreview != null ||
      documentationSlotsHavePages(slots) ||
      (sp.sourcePages?.length ?? 0) > 0 ||
      hasSlotFileId ||
      (draftId != null && metadataFiles.length > 0) ||
      showGlobalDocBar)

  return (
    <ModalOverlay isOpen={isOpen && !!sp} onClose={onClose} zIndex={25}>
      {sp ? (
      <div className={styles.overlayPanel}>
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Cerrar">
            <X size={20} strokeWidth={2} aria-hidden />
          </button>
        </div>
        <div className={styles.body}>
          {advisoryNotes.map((text, i) => (
            <p key={i} className={styles.advisory}>
              {text}
            </p>
          ))}
          <div className={cardStyles.projectAutofillSubprojectBody}>
            {!rubroUnresolvedNeedsPick(sp) && (sp.matchedSkillName || sp.matchScore != null) && (
              <div className={cardStyles.projectAutofillMatchHint}>
                {sp.matchedSkillName && <span>Coincidencia sugerida: {sp.matchedSkillName}</span>}
                {sp.matchScore != null && (
                  <span className={cardStyles.projectAutofillMatchScore}>
                    Puntaje:{' '}
                    {typeof sp.matchScore === 'number' ? sp.matchScore.toFixed(2) : sp.matchScore}
                  </span>
                )}
              </div>
            )}
            <div className={cardStyles.projectAutofillRubroSelect}>
              <CustomSelect
                value={sp.selectedSkillId ? String(sp.selectedSkillId) : ''}
                onChange={(value) => handleRubroSkillSelect(rid, value, sp)}
                label={rubroUnresolvedNeedsPick(sp) ? 'Rubro de la plataforma' : 'Rubro a cotizar'}
                outlineInvalid={
                  (sp.manualAdd && !rubroHasValidSelectedSkill(sp)) ||
                  (isRubroUnresolvedKind(sp.skillMatchKind) && !sp.selectedSkillId)
                }
                options={skillOptions.map((skill) => ({
                  value: String(skill.id),
                  label: skill.name
                }))}
                placeholder={
                  rubroUnresolvedNeedsPick(sp)
                    ? 'Elegí el rubro de la lista'
                    : 'Elegir rubro de la lista'
                }
                searchable={true}
                searchPlaceholder="Buscar rubro..."
                disabled={draftLocked}
                title={draftLocked ? PUBLISHED_HINT : undefined}
              />
            </div>
            <textarea
              value={sp.description || ''}
              onChange={(e) => updateRubro(rid, { description: e.target.value })}
              placeholder="Describe brevemente el trabajo a realizar..."
              disabled={draftLocked}
              title={draftLocked ? PUBLISHED_HINT : undefined}
            />
            {showRubroDoc ? (
              <RubroDocumentationBlock
                tempId={rid}
                slots={slots}
                sourcePages={sp.sourcePages}
                pdfBaseUrl={docPdfUrl || undefined}
                maxPage={docPageCount ?? undefined}
                fileIds={idsNorm}
                draftFiles={metadataFiles}
                disabled={!draftId || isSaving || draftLocked}
                title={draftLocked ? PUBLISHED_HINT : undefined}
                uploadingSlotKey={uploadingSlotKey}
                onSelectSlotFileId={(slot, fileId) => handleSelectRubroSlotFileId(rid, slot, fileId)}
                onSlotPagesChange={
                  docPageCount != null && draftId && !isSaving && !draftLocked
                    ? (slot, pages) => handleRubroSlotPagesChange(rid, slot, pages)
                    : undefined
                }
                onUploadSlotFile={(slot, file) => handleUploadRubroSlotFile(rid, slot, file)}
              />
            ) : null}
            {(sp.name || sp.link) && (
              <div className={cardStyles.projectAutofillSubprojectExtras}>
                {sp.name && <span>Nombre: {sp.name}</span>}
                {sp.link && (
                  <a
                    href={sp.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cardStyles.projectAutofillSubprojectLink}
                  >
                    Enlace
                  </a>
                )}
              </div>
            )}
            {isRubroFuzzyKind(sp.skillMatchKind) && (
              <label className={cardStyles.projectAutofillFuzzyConfirm}>
                <input
                  type="checkbox"
                  checked={!!fuzzyConfirmByRubro[rid]}
                  disabled={draftLocked}
                  title={draftLocked ? PUBLISHED_HINT : undefined}
                  onChange={(e) =>
                    setFuzzyConfirmByRubro((prev) => ({
                      ...prev,
                      [rid]: e.target.checked
                    }))
                  }
                />
                <span>Confirmo que el rubro elegido es el correcto</span>
              </label>
            )}
            <div className={cardStyles.projectAutofillRubroDates}>
              {sp.datesInheritedFromProject === true && (
                <span className={cardStyles.projectAutofillInheritedDatesHint}>
                  Mismas fechas que el proyecto
                </span>
              )}
              <label className={cardStyles.projectAutofillRubroDateLabel}>
                <span>Fecha inicio de recepción de presupuestos</span>
                <input
                  type="date"
                  value={sp.startDate || ''}
                  min={projectStartDate || today}
                  max={projectEndDate || undefined}
                  title={draftLocked ? PUBLISHED_HINT : undefined}
                  onChange={(e) => {
                    let nextStart = e.target.value
                    if (projectEndDate && nextStart) {
                      nextStart = clampIsoDateToMax(nextStart, projectEndDate)
                    }
                    let nextEnd = sp.endDate
                    if (projectEndDate && nextEnd) {
                      nextEnd = clampIsoDateToMax(nextEnd, projectEndDate)
                    }
                    if (nextEnd && nextStart && nextEnd < nextStart) {
                      nextEnd = nextStart
                    }
                    updateRubro(rid, {
                      startDate: nextStart,
                      endDate: nextEnd,
                      datesInheritedFromProject: false
                    })
                  }}
                  disabled={draftLocked}
                />
              </label>
              <label className={cardStyles.projectAutofillRubroDateLabel}>
                <span>Fecha fin de recepción de presupuestos</span>
                <input
                  type="date"
                  value={sp.endDate || ''}
                  min={sp.startDate || projectStartDate || today}
                  max={projectEndDate || undefined}
                  title={draftLocked ? PUBLISHED_HINT : undefined}
                  onChange={(e) => {
                    let v = e.target.value
                    if (projectEndDate && v) v = clampIsoDateToMax(v, projectEndDate)
                    const s = sp.startDate
                    if (s && v && v < s) v = s
                    updateRubro(rid, {
                      endDate: v,
                      datesInheritedFromProject: false
                    })
                  }}
                  disabled={draftLocked}
                />
              </label>
            </div>
            <select
              value={sp.status || 'OPEN'}
              disabled={draftLocked}
              title={draftLocked ? PUBLISHED_HINT : undefined}
              onChange={(e) =>
                updateRubro(rid, { status: e.target.value as 'OPEN' | 'CLOSE' })
              }
            >
              <option value="OPEN">Abierto</option>
              <option value="CLOSE">Cerrado</option>
            </select>
          </div>
        </div>
      </div>
      ) : null}
    </ModalOverlay>
  )
}

export default ProjectAutofillRubroDetailModal
